import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, BrainCircuit, Workflow, Code2, ArrowDown } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CaseStudyCard } from "@/components/CaseStudyCard";
import TrustBand from "@/components/TrustBand";
import FeaturedCase from "@/components/FeaturedCase";
import { SERVICES, CASE_STUDIES, MANIFESTO, CONTACT } from "@/data/site";

const SERVICE_ICONS = { "01": BrainCircuit, "02": Workflow, "03": Code2 };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBrace = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yWatermark = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 10]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.span
        aria-hidden="true"
        style={{ y: yBrace, rotate }}
        className="pointer-events-none absolute -right-6 top-16 hidden font-display text-[16rem] font-extrabold text-outline-coral opacity-60 lg:block"
      >
        {"}"}
      </motion.span>
      <motion.span
        aria-hidden="true"
        style={{ y: yWatermark }}
        className="pointer-events-none absolute -left-4 bottom-0 select-none font-display text-[22vw] font-extrabold leading-none text-outline lg:text-[16rem]"
      >
        REALM
      </motion.span>

      <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-24 sm:px-8 sm:pt-32 lg:pb-32">
        <MaskedLine delay={0.05}>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral" data-testid="hero-kicker">
            {"{ codeRealm }"} — AI · Automation · Full-Stack
          </span>
        </MaskedLine>

        <h1 className="mt-8 break-words font-display text-[2.5rem] font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.15}>AI that thinks.</MaskedLine>
          <MaskedLine delay={0.28}>Workflows that <span className="text-coral">run.</span></MaskedLine>
          <MaskedLine delay={0.41}>Apps that <span className="italic text-outline">fly.</span></MaskedLine>
        </h1>

        <Reveal delay={0.55} className="mt-10 max-w-xl">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            CodeRealm designs and builds production AI features, n8n automations, 
            and React full-stack apps for SaaS and ops-led teams — end to end, 
            without the agency theater.
          </p>
        </Reveal>

        <Reveal delay={0.65} className="mt-6">
          <p className="font-mono text-sm text-muted-foreground">
            For founders, CTOs, and product/ops leads who need outcomes, not slide decks.
          </p>
        </Reveal>

        <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            data-testid="hero-start-project-button"
            className="group flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
          >
            Start a project
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/case-studies"
            data-testid="hero-case-studies-button"
            className="flex items-center gap-2 rounded-full border border-foreground/20 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-coral hover:text-coral"
          >
            See case studies
          </Link>
        </Reveal>

        <Reveal delay={0.85} className="mt-12">
          <p className="font-mono text-xs text-muted-foreground/80">
            Boutique studio · CodeRealm World Services LLP · India + remote
          </p>
        </Reveal>

        <Reveal delay={0.95} className="mt-12 flex items-center gap-3 text-muted-foreground">
          <ArrowDown className="h-4 w-4 animate-bounce" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">Scroll — the realm goes deep</span>
        </Reveal>
      </div>
    </section>
  );
};

const Services = () => (
  <section data-testid="services-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <Reveal>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">What we do</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Three crafts.<br />One standard: shipped.
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          Every engagement is led by senior engineers. No hand-offs to juniors,
          no black boxes — just systems you can read, run and own.
        </p>
      </div>
    </Reveal>

    <div className="mt-16 border-t border-border">
      {SERVICES.map((service, i) => {
        const Icon = SERVICE_ICONS[service.index];
        return (
          <Reveal key={service.id} delay={i * 0.08}>
            <div
              data-testid={`service-row-${service.id}`}
              className="group grid gap-6 border-b border-border py-10 transition-colors duration-500 hover:bg-card sm:grid-cols-[80px_1fr_1.6fr_auto] sm:items-center sm:gap-10 sm:px-6"
            >
              <span className="font-display text-4xl font-extrabold text-outline transition-colors duration-500 group-hover:text-coral group-hover:[-webkit-text-stroke:0px]">
                {service.index}
              </span>
              <div>
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-coral" />
                  <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{service.title}</h3>
                </div>
                <span className="mt-2 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {service.tag}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{service.description}</p>
              <div className="flex max-w-[220px] flex-wrap gap-2">
                {service.chips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground transition-colors duration-300 group-hover:border-coral/50 group-hover:text-foreground"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const FeaturedWork = () => (
  <section data-testid="featured-work-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <Reveal>
      <div className="flex items-end justify-between gap-6">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Selected work</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Case studies
          </h2>
        </div>
        <Link
          to="/case-studies"
          data-testid="featured-work-view-all-link"
          className="group hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors duration-300 hover:text-coral sm:flex"
        >
          View all
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </Reveal>
    <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {CASE_STUDIES.map((study, i) => (
        <Reveal key={study.id} delay={i * 0.1}>
          <CaseStudyCard study={study} index={i} />
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="manifesto-section" className="border-y border-border bg-card/60">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <Reveal>
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">The realm manifesto</span>
      </Reveal>
      <div className="mt-12">
        {MANIFESTO.map((chapter, i) => (
          <Reveal key={chapter.index} delay={i * 0.08}>
            <div
              data-testid={`manifesto-chapter-${chapter.index}`}
              className="group grid gap-4 border-b border-border py-10 first:border-t sm:grid-cols-[160px_1fr_1.4fr] sm:items-baseline sm:gap-10"
            >
              <span className="font-display text-6xl font-extrabold text-outline transition-all duration-500 group-hover:text-coral group-hover:[-webkit-text-stroke:0px] sm:text-7xl">
                {chapter.index}
              </span>
              <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{chapter.title}</h3>
              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{chapter.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CeoSpotlight = () => (
  <section data-testid="ceo-spotlight-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
      <Reveal className="relative mx-auto w-full max-w-md lg:mx-0">
        <div aria-hidden="true" className="absolute -inset-3 rounded-3xl border border-coral/40" />
        <div className="relative overflow-hidden rounded-2xl">
          <img
            src="/ceo.png"
            alt="Soumitra Mukherjee — CEO & Founder of codeRealm World Services LLP"
            className="aspect-square w-full object-cover"
            loading="lazy"
            data-testid="ceo-portrait"
          />
          <span className="absolute bottom-4 left-4 rounded-full bg-background/85 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] backdrop-blur-md">
            CEO & Founder
          </span>
        </div>
      </Reveal>
      <div>
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Leadership</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Soumitra Mukherjee
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            A software architect who has spent his career turning gnarly business
            problems into elegant, self-running systems. Soumitra leads every
            codeRealm engagement personally — from the first architecture sketch
            to the last production deploy.
          </p>
          <blockquote className="mt-8 border-l-2 border-coral pl-6 font-display text-xl font-bold leading-snug tracking-tight sm:text-2xl">
            "Automation should feel like magic and read like documentation."
          </blockquote>
        </Reveal>
        <Reveal delay={0.2} className="mt-8">
          <Link
            to="/about"
            data-testid="ceo-about-link"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-coral"
          >
            More about the studio
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section data-testid="cta-section" className="border-t border-border">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <Reveal>
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Next step</span>
        <h2 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Have a system that should <span className="text-coral">build itself?</span>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
          Tell us where your team loses hours. We'll reply with a concrete plan —
          scope, stack and timeline — not a sales deck.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="group flex items-center gap-2 rounded-full bg-coral px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
          >
            Talk to us
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="cta-email-link"
            className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground underline-offset-4 transition-colors duration-300 hover:text-coral hover:underline"
          >
            {CONTACT.email}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="codeRealm — AI, n8n Automation & React Full-Stack Studio"
        siteName="codeRealm"
        description="codeRealm World Services LLP builds production AI systems, self-healing n8n automations and ultra-fast React applications. Led by CEO Soumitra Mukherjee."
        image="/logo-light.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "codeRealm World Services LLP",
          url: "https://coderealm.codes",
          email: "contactus@coderealm.codes",
          telephone: "+91-8744807237",
          founder: { "@type": "Person", name: "Soumitra Mukherjee" },
        }}
      />
      <Hero />
      <TrustBand />
      <FeaturedCase />
      <Marquee />
      <Services />
      <FeaturedWork />
      <Manifesto />
      <CeoSpotlight />
      <Cta />
    </>
  );
}
